"use client";

/**
 * Kuttos Fashion — "The Edit" (marketplace shop).
 *
 * Editorial cross-brand browse on ivory paper. URL-driven filters (category,
 * brand, search, minPrice, maxPrice, color, sortBy, page) so every view is
 * shareable; data via the subscription-gated getMarketplaceProducts /
 * getMarketplaceFilterOptions queries — locked brands never appear.
 */

import Link from "next/link";
import { useCallback, useMemo, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import { ArrowLeft, ArrowRight, Search, X } from "lucide-react";

const ugx = (n: number) => `UGX ${Math.round(n).toLocaleString()}`;

const SORTS = [
  { value: "newest", label: "Newest" },
  { value: "price_asc", label: "Price — low to high" },
  { value: "price_desc", label: "Price — high to low" },
];

export function ShopPageClient() {
  const router = useRouter();
  const params = useSearchParams();

  const category = params?.get("category") || "All";
  const brand = params?.get("brand") || "All";
  const search = params?.get("search") || "";
  const minPrice = params?.get("minPrice") || "";
  const maxPrice = params?.get("maxPrice") || "";
  const sortBy = params?.get("sortBy") || "newest";
  const page = Math.max(1, parseInt(params?.get("page") || "1", 10) || 1);

  const [searchDraft, setSearchDraft] = useState(search);

  // @ts-ignore deep instantiation
  const data = useQuery(api.marketing.fashion.getMarketplaceProducts, {
    category: category !== "All" ? category : undefined,
    brand: brand !== "All" ? brand : undefined,
    search: search || undefined,
    minPrice: minPrice ? Number(minPrice) : undefined,
    maxPrice: maxPrice ? Number(maxPrice) : undefined,
    sortBy,
    page,
    limit: 24,
  });

  // @ts-ignore deep instantiation
  const filterOptions = useQuery(api.marketing.fashion.getMarketplaceFilterOptions, {
    category: category !== "All" ? category : undefined,
  });

  const products = (data as any)?.products as any[] | undefined;
  const pagination = (data as any)?.pagination;

  const setParams = useCallback(
    (patch: Record<string, string | undefined>, resetPage = true) => {
      const next = new URLSearchParams(params?.toString() || "");
      for (const [k, v] of Object.entries(patch)) {
        if (v === undefined || v === "" || v === "All") next.delete(k);
        else next.set(k, v);
      }
      if (resetPage) next.delete("page");
      router.replace(`/shop${next.toString() ? `?${next.toString()}` : ""}`, { scroll: false });
    },
    [params, router]
  );

  const categories = useMemo(() => {
    const fromServer = ((filterOptions as any)?.categories || []) as { name: string; slug: string }[];
    return [{ name: "Everything", slug: "All" }, ...fromServer];
  }, [filterOptions]);

  const brandNames = ((filterOptions as any)?.brands || []) as string[];

  const activeFilters = [
    search && { key: "search", label: `“${search}”` },
    brand !== "All" && { key: "brand", label: brand },
    minPrice && { key: "minPrice", label: `from ${ugx(Number(minPrice))}` },
    maxPrice && { key: "maxPrice", label: `to ${ugx(Number(maxPrice))}` },
  ].filter(Boolean) as { key: string; label: string }[];

  return (
    <div style={{ background: "var(--mk-paper)" }} className="min-h-screen">
      {/* ── Title band ── */}
      <section className="mx-auto max-w-[1440px] px-4 md:px-8 pt-10 md:pt-14 pb-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <p className="mk-eyebrow mk-rise">Kuttos Fashion — all houses</p>
            <h1 className="mk-display mk-rise mk-d1 mt-3" style={{ fontSize: "clamp(40px, 5.6vw, 76px)" }}>
              The <em>Edit</em>
            </h1>
          </div>
          <form
            className="mk-rise mk-d2 flex items-stretch w-full md:w-[360px]"
            onSubmit={(e) => {
              e.preventDefault();
              setParams({ search: searchDraft.trim() || undefined });
            }}
          >
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: "var(--mk-ink-faint)" }} />
              <input
                value={searchDraft}
                onChange={(e) => setSearchDraft(e.target.value)}
                placeholder="Search pieces, fabrics, styles…"
                className="mk-input w-full"
                style={{ paddingLeft: 44 }}
                aria-label="Search products"
              />
            </div>
            <button type="submit" className="mk-btn mk-btn--solid" style={{ paddingTop: 0, paddingBottom: 0 }}>
              Go
            </button>
          </form>
        </div>
      </section>

      {/* ── Filter bar ── */}
      <div
        className="sticky top-16 z-30"
        style={{
          background: "rgba(246,242,234,0.95)",
          backdropFilter: "blur(10px)",
          borderTop: "1px solid var(--mk-line)",
          borderBottom: "1px solid var(--mk-line)",
        }}
      >
        <div className="mx-auto max-w-[1440px] px-4 md:px-8 py-3 flex items-center gap-3 overflow-x-auto">
          {categories.map((c) => (
            <button
              key={c.slug}
              onClick={() => setParams({ category: c.slug })}
              className={`mk-chip ${category === c.slug || (c.slug === "All" && category === "All") ? "mk-chip--on" : ""}`}
            >
              {c.name}
            </button>
          ))}
          <span className="h-6 w-px shrink-0" style={{ background: "var(--mk-line)" }} />
          <select
            value={brand}
            onChange={(e) => setParams({ brand: e.target.value })}
            className="mk-select shrink-0"
            style={{ paddingTop: 8, paddingBottom: 8 }}
            aria-label="Filter by brand"
          >
            <option value="All">All houses</option>
            {brandNames.map((b) => (
              <option key={b} value={b}>{b}</option>
            ))}
          </select>
          <select
            value={sortBy}
            onChange={(e) => setParams({ sortBy: e.target.value }, false)}
            className="mk-select shrink-0"
            style={{ paddingTop: 8, paddingBottom: 8 }}
            aria-label="Sort"
          >
            {SORTS.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* ── Results ── */}
      <section className="mx-auto max-w-[1440px] px-4 md:px-8 py-10 pb-20">
        <div className="flex items-center justify-between gap-4 mb-7">
          <p className="mk-eyebrow" style={{ fontSize: 10 }}>
            {pagination ? `${pagination.total} piece${pagination.total === 1 ? "" : "s"}` : "Loading the rail…"}
          </p>
          {activeFilters.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap justify-end">
              {activeFilters.map((f) => (
                <button
                  key={f.key}
                  onClick={() => {
                    if (f.key === "search") setSearchDraft("");
                    setParams({ [f.key]: undefined });
                  }}
                  className="mk-chip mk-chip--on"
                  style={{ textTransform: "none", letterSpacing: "0.02em" }}
                >
                  {f.label} <X className="h-3 w-3" />
                </button>
              ))}
            </div>
          )}
        </div>

        {products === undefined ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 gap-y-10">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i}>
                <div className="mk-skeleton" style={{ aspectRatio: "3/4" }} />
                <div className="mk-skeleton mt-4 h-3 w-1/3" />
                <div className="mk-skeleton mt-2 h-4 w-2/3" />
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="mk-frame p-16 text-center">
            <p className="mk-display" style={{ fontSize: 28 }}>Nothing on this rail<em>—yet</em></p>
            <p className="mt-3 text-sm" style={{ color: "var(--mk-ink-soft)" }}>
              Try a different department, or clear your filters.
            </p>
            <button
              onClick={() => {
                setSearchDraft("");
                router.replace("/shop", { scroll: false });
              }}
              className="mk-btn mt-8"
            >
              Clear everything
            </button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 gap-y-10">
              {products.map((p: any) => {
                const main = p.images?.[0];
                const alt = p.images?.[1];
                return (
                  <Link key={p._id} href={`/product/${p.slug}`} className="mk-card group">
                    <div className="mk-card__media">
                      {p.compareAtPrice && p.compareAtPrice > p.price && (
                        <span className="mk-card__stamp mk-stamp" style={{ background: "var(--mk-gold)" }}>
                          Offer
                        </span>
                      )}
                      {main ? (
                        <>
                          <img src={main} alt={p.name} loading="lazy" className={`mk-card__main ${alt ? "mk-has-alt" : ""}`} />
                          {alt && <img src={alt} alt="" loading="lazy" aria-hidden className="mk-card__alt" />}
                        </>
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="mk-eyebrow">{p.brandName || "Kuttos"}</span>
                        </div>
                      )}
                    </div>
                    <p className="mk-card__brand">{p.brandName}</p>
                    <h3 className="mk-card__name">{p.name}</h3>
                    <p className="mk-card__price">
                      {ugx(p.price)}
                      {p.compareAtPrice && p.compareAtPrice > p.price ? <s>{ugx(p.compareAtPrice)}</s> : null}
                    </p>
                  </Link>
                );
              })}
            </div>

            {/* Pagination */}
            {pagination && pagination.pages > 1 && (
              <div className="mt-14 flex items-center justify-center gap-6">
                <button
                  disabled={!pagination.hasPrev}
                  onClick={() => setParams({ page: String(page - 1) }, false)}
                  className="mk-btn disabled:opacity-30 disabled:pointer-events-none"
                >
                  <ArrowLeft className="h-4 w-4" /> Prev
                </button>
                <span className="mk-eyebrow" style={{ fontSize: 11 }}>
                  Page {pagination.page} of {pagination.pages}
                </span>
                <button
                  disabled={!pagination.hasNext}
                  onClick={() => setParams({ page: String(page + 1) }, false)}
                  className="mk-btn disabled:opacity-30 disabled:pointer-events-none"
                >
                  Next <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}
